import clsx from 'clsx';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';

import type { AllDaySectionProps, CalendarEventWithColor } from '../../types';

import s from './AllDaySection.module.scss';

const MAX_VISIBLE_EVENTS = 3;

const AllDaySection: React.FC<AllDaySectionProps> = observer(({ days, onEventClick }) => {
  // Check if any day has all-day events
  const hasAnyAllDayEvents = days.some((day) => day.allDayEvents.length > 0);

  if (!hasAnyAllDayEvents) {
    return null;
  }

  return (
    <div className={s.all_day_section}>
      {/* Label column aligned with time column */}
      <div className={s.all_day_section__label}>
        <span>Весь день</span>
      </div>

      {/* Day columns */}
      <div className={s.all_day_section__days}>
        {days.map((day, index) => (
          <AllDayDayColumn
            key={index}
            events={day.allDayEvents}
            isToday={day.date.hasSame(DateTime.local(), 'day')}
            isWeekend={day.date.weekday >= 6}
            onEventClick={onEventClick}
          />
        ))}
      </div>
    </div>
  );
});

interface AllDayDayColumnProps {
  events: CalendarEventWithColor[];
  isToday: boolean;
  isWeekend: boolean;
  onEventClick: (event: CalendarEventWithColor) => void;
}

const AllDayDayColumn: React.FC<AllDayDayColumnProps> = ({
  events,
  isToday,
  isWeekend,
  onEventClick,
}) => {
  const [expanded, setExpanded] = useState(false);

  const visibleEvents = expanded ? events : events.slice(0, MAX_VISIBLE_EVENTS);
  const hiddenCount = events.length - MAX_VISIBLE_EVENTS;
  const showMoreButton = !expanded && hiddenCount > 0;

  return (
    <div
      className={clsx(s.all_day_section__day_column, {
        [s.all_day_section__day_column_today]: isToday,
        [s.all_day_section__day_column_weekend]: isWeekend && !isToday,
      })}
    >
      {visibleEvents.map((event) => (
        <AllDayEventChip key={event.id} event={event} onClick={() => onEventClick(event)} />
      ))}

      {showMoreButton && (
        <button
          className={s.all_day_section__more_button}
          onClick={() => setExpanded(true)}
          type="button"
        >
          +{hiddenCount} ещё
        </button>
      )}

      {expanded && hiddenCount > 0 && (
        <button
          className={s.all_day_section__more_button}
          onClick={() => setExpanded(false)}
          type="button"
        >
          Свернуть
        </button>
      )}
    </div>
  );
};

interface AllDayEventChipProps {
  event: CalendarEventWithColor;
  onClick: () => void;
}

const AllDayEventChip: React.FC<AllDayEventChipProps> = ({ event, onClick }) => {
  const isConfirmed = event.myResponseStatus === null || event.myResponseStatus === 'ACCEPTED';

  return (
    <div
      className={clsx(s.all_day_event, {
        [s.all_day_event_confirmed]: isConfirmed,
      })}
      style={
        {
          '--event-color': event.color,
        } as React.CSSProperties
      }
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className={s.all_day_event__title}>{event.summary}</span>
    </div>
  );
};

export { AllDaySection };
